import { useMemo, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import s from "./Dashboard.module.css";
import { Page } from "../../layout/AppShell";
import { useSession } from "../../app/SessionContext";
import { useAlerts, useKpis, useWorkOrders } from "../../api/queries";
import { sortAlerts } from "../../domain/alerts";
import { gradeKpi, overallGrade } from "../../domain/kpis";
import {
  ageDays,
  isOpen,
  STATUS_LABEL,
  summarise,
  WORK_ORDER_PRIORITIES,
  WORK_ORDER_STATUSES,
} from "../../domain/workOrders";
import {
  Button,
  Card,
  ErrorState,
  gradeColour,
  PriorityBadge,
  SeverityBadge,
  Skeleton,
  StatusBadge,
} from "../../design/components";
import { KpiTile } from "./KpiTile";
import { KpiTargetsCard } from "../entryForms/KpiTargetsCard";
import { formatDate } from "../../lib/format";
import { t } from "../../i18n";

export function DashboardPage() {
  const { site, serverTime } = useSession();
  const kpis = useKpis();
  /* build 6B: the targets the measures are graded against, opened from the KPI card */
  const [showTargets, setShowTargets] = useState(false);
  const orders = useWorkOrders();
  const alerts = useAlerts();
  const openAlerts = useMemo(() => sortAlerts(alerts.data ?? []), [alerts.data]);

  const graded = useMemo(() => (kpis.data ?? []).map(gradeKpi), [kpis.data]);
  const overall = useMemo(() => overallGrade(graded), [graded]);
  const now = useMemo(() => new Date(serverTime), [serverTime]);
  const summary = useMemo(() => summarise(orders.data?.items ?? [], now), [orders.data, now]);

  const attention = useMemo(
    () =>
      (orders.data?.items ?? [])
        .filter((w) => isOpen(w.status))
        .sort((a, b) => a.priority - b.priority || ageDays(b, now) - ageDays(a, now))
        .slice(0, 6),
    [orders.data, now],
  );

  return (
    <Page eyebrow={t("{code} · overview", { code: site.code })} title="The state of the airport in one screen">
      <div className={s.summary}>
        <Stat label={t("Open work orders")} value={summary.open} to="/work-orders" />
        <Stat
          label={t("P1 open")}
          value={summary.p1Open}
          colour={summary.p1Open ? "var(--p1)" : undefined}
          to="/work-orders?priority=P1"
        />
        <Stat
          label={t("Overdue against priority")}
          value={summary.overdue}
          colour={summary.overdue ? "var(--amber)" : undefined}
          to="/work-orders?overdue=1"
        />
        <Stat
          label={t("Awaiting verification")}
          value={summary.byStatus.completed}
          to="/work-orders?status=completed"
        />
        <Stat
          label={t("Open alerts")}
          value={alerts.data ? alerts.data.length : (site.counts?.openAlerts ?? "—")}
          colour={(alerts.data?.length ?? site.counts?.openAlerts) ? "var(--red)" : undefined}
          to="/alerts"
        />
      </div>

      <Card
        title="Executive KPIs"
        sub="each measure against a shown target, graded, with its source"
        actions={
          overall.grade && (
            <div className={s.overall}>
              <div className={s.overallGrade} style={{ color: gradeColour(overall.grade) }}>
                {overall.grade}
              </div>
              <div className={s.overallText}>
                <span>
                  {t("{pct}% of target across {n} measures", {
                    pct: Math.round((overall.pct ?? 0) * 100),
                    n: overall.scored,
                  })}
                </span>
                {overall.notComputable > 0 && (
                  <span>{t("{n} not computable — excluded, not counted as zero", { n: overall.notComputable })}</span>
                )}
                {overall.noTarget > 0 && (
                  <span>{t("{n} with no declared target — shown, not graded", { n: overall.noTarget })}</span>
                )}
                {overall.proposed > 0 && (
                  <span>{t("{n} graded against proposed targets, not yet agreed", { n: overall.proposed })}</span>
                )}
              </div>
            </div>
          )
        }
      >
        {kpis.isPending ? (
          <Skeleton rows={4} height={40} />
        ) : kpis.isError ? (
          <ErrorState error={kpis.error} retry={() => void kpis.refetch()} />
        ) : (
          <div className={s.grid}>
            {graded.map((k) => (
              <KpiTile key={k.key} kpi={k} />
            ))}
          </div>
        )}
        <div style={{ marginTop: 10 }}>
          <Button size="sm" variant="ghost" onClick={() => setShowTargets((v) => !v)} aria-expanded={showTargets}>
            {showTargets ? t("Hide targets") : t("See or set targets")}
          </Button>
        </div>
      </Card>
      {showTargets && <KpiTargetsCard collapsible={false} />}

      <div className={s.split}>
        <Card
          title="Needs attention"
          sub="open work, highest priority and oldest first"
          actions={<Link to="/work-orders">{t("All work orders →")}</Link>}
        >
          {orders.isPending ? (
            <Skeleton rows={5} />
          ) : orders.isError ? (
            <ErrorState error={orders.error} retry={() => void orders.refetch()} />
          ) : attention.length === 0 ? (
            <div className="muted">{t("No open work orders at {code}.", { code: site.code })}</div>
          ) : (
            <div className={s.list}>
              {attention.map((w) => (
                <Link key={w.id} to={`/work-orders/${w.id}`} className={s.listRow}>
                  <PriorityBadge priority={w.priority} />
                  <span>
                    <b className="mono">{w.number}</b> · {w.title}
                    {w.crew && <span className="muted"> — {w.crew}</span>}
                  </span>
                  <span className={s.listMeta}>
                    <StatusBadge status={w.status} /> · {t("{n}d", { n: ageDays(w, now) })} · {formatDate(w.openedAt)}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </Card>

        <Card
          title="Open alerts"
          sub="what the rules raised, most severe first"
          actions={<Link to="/alerts">{t("Why did these fire? →")}</Link>}
        >
          {alerts.isPending ? (
            <Skeleton rows={3} />
          ) : alerts.isError ? (
            <ErrorState error={alerts.error} retry={() => void alerts.refetch()} />
          ) : openAlerts.length === 0 ? (
            <div className="muted">{t("No open alerts at {code}.", { code: site.code })}</div>
          ) : (
            <div className={s.list}>
              {openAlerts.slice(0, 5).map((a) => (
                <Link key={a.id} to={`/alerts?open=${a.id}`} className={s.listRow}>
                  <SeverityBadge severity={a.severity} />
                  <span>{a.title}</span>
                  <span className={s.listMeta}>
                    {a.kind === "predictive" ? t("PREDICTIVE") : t("REACTIVE")} · {formatDate(a.raisedAt)}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </Card>

        <Card title="Work by state" sub="the lifecycle, counted">
          {orders.isPending ? (
            <Skeleton rows={6} />
          ) : (
            <div className={s.bars}>
              {WORK_ORDER_STATUSES.map((st) => (
                <Bar
                  key={st}
                  label={t(STATUS_LABEL[st])}
                  value={summary.byStatus[st]}
                  max={Math.max(1, summary.total)}
                />
              ))}
              <div style={{ height: 8 }} />
              {WORK_ORDER_PRIORITIES.map((p) => (
                <Bar
                  key={p}
                  label={`P${p}`}
                  value={summary.byPriority[p]}
                  max={Math.max(1, summary.total)}
                  colour={`var(--p${p})`}
                />
              ))}
            </div>
          )}
        </Card>
      </div>
    </Page>
  );
}

function Stat({ label, value, colour, to }: { label: string; value: number | string; colour?: string; to?: string }) {
  const body = (
    <>
      <div className={s.statValue} style={{ "--stat-c": colour } as CSSProperties}>
        {value}
      </div>
      <div className={s.statLabel}>{label}</div>
    </>
  );
  return to ? (
    <Link to={to} className={s.stat}>
      {body}
    </Link>
  ) : (
    <div className={s.stat}>{body}</div>
  );
}

function Bar({ label, value, max, colour }: { label: string; value: number; max: number; colour?: string }) {
  return (
    <div className={s.bar}>
      <span className="muted">{label}</span>
      <div className={s.barTrack}>
        <div className={s.barFill} style={{ width: `${(value / max) * 100}%`, "--bar-c": colour } as CSSProperties} />
      </div>
      <span className="mono" style={{ textAlign: "end" }}>
        {value}
      </span>
    </div>
  );
}
